<template>
    <confirmation-dialog
        ref="modal"
        label="Vestiging verwijderen"
        description="Weet je zeker dat je deze vestiging wilt verwijderen?"
    >
        <template #default>
            <text-heading>Vestiging verwijderen</text-heading>
            <p>
                <template v-if="checkingCanDelete">
                    <spinner-loader />
                </template>
                <template v-else-if="canDeleteLegacy && canDelete">
                    Weet je zeker dat je deze vestiging wilt verwijderen?
                </template>
                <template v-else>
                    Je kunt deze vestiging niet verwijderen omdat deze in gebruik is.
                </template>
            </p>
        </template>

        <template #actions="{ bindings }">
            <flex-container align-items="center" justify-content="flex-end">
                <template v-if="checkingCanDelete || (canDeleteLegacy && canDelete)">
                    <button-text
                        @click="bindings.overlay.closeOverlay()"
                    >
                        Annuleren
                    </button-text>
                    <button-block
                        :color="'red-500'"
                        :disabled="checkingCanDelete"
                        @click="removeTeam"
                    >
                        Verwijderen
                    </button-block>
                </template>
                <template v-else>
                    <button-block
                        @click="bindings.overlay.closeOverlay()"
                    >
                        Sluiten
                    </button-block>
                </template>
            </flex-container>
        </template>
    </confirmation-dialog>
</template>

<script setup lang="ts">
    import {
        ButtonBlock,
        ButtonText,
        ConfirmationDialog,
        type ExposedOverlay,
        FlexContainer,
        NotificationBlock,
        SpinnerLoader,
        useInteractionBlocker,
        useNotify,
    } from '@keyvue/one-ui';
    import { computed, onMounted, ref } from 'vue';
    import useOverlayAsRoute from '@/services/keyvue-ui/composables/useOverlayAsRoute';
    import { useRouter } from 'vue-router';
    import { BranchAPI } from '@keyvue/one-api-client-legacy';
    import { CanDeleteTeamCommand, TeamId } from '@keyvue/one-api-client';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import useEventBus from '@/services/eventbus/composables/useEventBus';
    import TeamDeletedEvent, { type TeamDeletedEventParameters } from '@/company/team/events/TeamDeletedEvent';

    const modal = ref<ExposedOverlay>();
    const router = useRouter();
    const { client } = useClient();
    const eventBus = useEventBus();
    const { notify } = useNotify();
    const { blockInteractionForLoad } = useInteractionBlocker();

    const teamId = computed<string>(() => router.currentRoute.value.params.teamId.toString());
    const checkingCanDelete = ref(true);
    const canDeleteLegacy = ref(false);
    const canDelete = ref(false);

    useOverlayAsRoute({
        overlay: modal,
        isSideSheet: false,
        redirectMethod: async () => {
            await new Promise((resolve) => {
                setTimeout(resolve, 1000);
            });

            await router.push({ name: 'team-list' });
        },
    });

    async function checkCanDelete() {
        checkingCanDelete.value = true;

        [canDeleteLegacy.value, canDelete.value] = await Promise.all([
            BranchAPI.canDelete(teamId.value),
            client.dispatch(new CanDeleteTeamCommand(new TeamId(teamId.value))),
        ]);

        checkingCanDelete.value = false;
    }

    async function removeTeam() {
        const clearBlock = blockInteractionForLoad('Bezig met het verwijderen van de vestiging');

        try {
            await BranchAPI.delete(
                teamId.value,
            );
        } catch (e) {
            notify({
                component: NotificationBlock,
                props: {
                    title: 'We konden de vestiging niet verwijderen',
                    text: 'Probeer het later nog een keer.',
                    color: 'red-500',
                },
                duration: 4000,
            });
        }

        clearBlock();

        notify({
            component: NotificationBlock,
            props: {
                title: 'Gelukt!',
                text: 'We hebben de vestiging verwijderd',
                color: 'green-500',
            },
            duration: 4000,
        });

        eventBus.dispatch<TeamDeletedEventParameters>(
            TeamDeletedEvent,
            {
                teamId: teamId.value,
            },
        );

        await router.push({ name: 'team-list' });
    }

    onMounted(() => {
        checkCanDelete();
    });
</script>
